import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

interface SessionContextType {
  employeeId: string;
  setEmployeeId: (id: string) => void;
  locationId?: string;
  setLocationId: (id: string) => void;
  userId?: string;
  userName?: string;
  userEmail?: string;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const LOCATION_STORAGE_KEY = 'tlt_location_id';

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const employeeId = useMemo(() => {
    return user?.userId || '';
  }, [user?.userId]);

  const [locationId, setLocationIdState] = useState<string>(() => {
    const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (stored) {
      try {
        return stored;
      } catch {
        return undefined;
      }
    }
    return undefined;
  });

  useEffect(() => {
    if (locationId) {
      localStorage.setItem(LOCATION_STORAGE_KEY, locationId);
    } else {
      localStorage.removeItem(LOCATION_STORAGE_KEY);
    }
  }, [locationId]);

  const setEmployeeId = (_id: string) => {
    // Deprecated: Employee ID is now retrieved from /api/v1/auth/me
  };

  const setLocationId = (id: string) => {
    setLocationIdState(id);
  };

  return (
    <SessionContext.Provider
      value={{
        employeeId,
        setEmployeeId,
        locationId,
        setLocationId,
        userId: user?.userId,
        userName: user?.name,
        userEmail: user?.email,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
